import type { EnhancedPuzzle, PuzzleData } from '@/types/puzzle';

// New content pack:
// - Finishes Emberdeep (level 53)
// - Adds Arcana (levels 54-63) featuring teleporters (`t`)

export const emberdeepFinale: PuzzleData[] = [
  {
    grid: [
      'o---n-',
      '-xx---',
      '-xlx-n',
      '--x--n',
      'x--x--',
      '----ex',
    ],
    metadata: {
      level: 53,
      name: 'Emberdeep Exit',
      difficulty: 'hard',
      theme: 'lava',
      collectibles: [
        { type: 'coin', position: [1, 0], value: 10, id: 'L53_C1' },
        { type: 'coin', position: [4, 3], value: 10, id: 'L53_C2' },
        { type: 'gem', position: [1, 5], value: 50, id: 'L53_G1' },
      ],
      minStars: 0,
      timeTarget: 55,
      stepTarget: 6,
    },
  } as EnhancedPuzzle,
];

export const arcanaPack: PuzzleData[] = [
  // Level 54: Teleporter intro (split chamber)
  {
    grid: [
      'o-x--',
      '--x--',
      '-tx-t',
      '--x--',
      '--xex',
    ],
    metadata: {
      level: 54,
      name: 'Split Chamber',
      difficulty: 'easy',
      theme: 'magical',
      collectibles: [
        { type: 'coin', position: [1, 0], value: 10, id: 'L54_C1' },
        { type: 'coin', position: [3, 2], value: 10, id: 'L54_C2' },
      ],
      minStars: 0,
      timeTarget: 35,
      stepTarget: 5,
    },
  } as EnhancedPuzzle,

  // Level 55: Teleporter + precision stop (use the ceiling rock to land on the exit)
  {
    grid: [
      'o-xx-',
      '--xex',
      '-tx-t',
      '--x--',
      '--x--',
    ],
    metadata: {
      level: 55,
      name: 'Keystone Lift',
      difficulty: 'easy',
      theme: 'magical',
      collectibles: [
        { type: 'coin', position: [1, 0], value: 10, id: 'L55_C1' },
        { type: 'gem', position: [0, 3], value: 50, id: 'L55_G1' },
      ],
      minStars: 0,
      timeTarget: 40,
      stepTarget: 6,
    },
  } as EnhancedPuzzle,

  // Level 56: Off-axis landing (teleport, then drop into the exit)
  {
    grid: [
      'o--x--',
      '---x-t',
      '--xxex',
      '-t-xx-',
      '---x--',
      '---x--',
    ],
    metadata: {
      level: 56,
      name: 'Off-Axis',
      difficulty: 'medium',
      theme: 'magical',
      collectibles: [
        { type: 'coin', position: [2, 0], value: 10, id: 'L56_C1' },
        { type: 'coin', position: [0, 4], value: 10, id: 'L56_C2' },
        { type: 'gem', position: [1, 2], value: 50, id: 'L56_G1' },
      ],
      minStars: 0,
      timeTarget: 55,
      stepTarget: 8,
    },
  } as EnhancedPuzzle,

  // Level 57: Teleporter jump-to-win
  {
    grid: [
      'o-xtex',
      '--x---',
      '------',
      '--s---',
      '-t-x--',
      '--x---',
    ],
    metadata: {
      level: 57,
      name: 'Blink',
      difficulty: 'medium',
      theme: 'magical',
      collectibles: [
        { type: 'coin', position: [1, 5], value: 10, id: 'L57_C1' },
        { type: 'coin', position: [4, 2], value: 10, id: 'L57_C2' },
      ],
      minStars: 0,
      timeTarget: 50,
      stepTarget: 8,
    },
  } as EnhancedPuzzle,

  // Level 58: Teleport across the seam, then drop onto the exit
  {
    grid: [
      '---x-o',
      '---x-t',
      '---x--',
      '-t-x--',
      '--ex--',
      '--xx--',
    ],
    metadata: {
      level: 58,
      name: 'Seamwalk',
      difficulty: 'medium',
      theme: 'magical',
      collectibles: [
        { type: 'coin', position: [1, 0], value: 10, id: 'L58_C1' },
        { type: 'gem', position: [0, 4], value: 50, id: 'L58_G1' },
      ],
      minStars: 0,
      timeTarget: 55,
      stepTarget: 9,
    },
  } as EnhancedPuzzle,

  // Level 59: Larger chamber with a required teleport
  {
    grid: [
      'o--x---',
      '---x--t',
      '--xx---',
      '---x-n-',
      '--xx---',
      't--x---',
      '---x-ex',
    ],
    metadata: {
      level: 59,
      name: 'Crossed Wires',
      difficulty: 'hard',
      theme: 'magical',
      collectibles: [
        { type: 'coin', position: [4, 3], value: 10, id: 'L59_C1' },
        { type: 'coin', position: [5, 5], value: 10, id: 'L59_C2' },
        { type: 'gem', position: [1, 6], value: 50, id: 'L59_G1' },
      ],
      minStars: 0,
      timeTarget: 70,
      stepTarget: 12,
    },
  } as EnhancedPuzzle,

  // Level 60: Stop tile staging into teleport
  {
    grid: [
      '--o-x---',
      '----x--t',
      '--s-x--n',
      '--t-x---',
      '----x-ex',
    ],
    metadata: {
      level: 60,
      name: 'Staging',
      difficulty: 'hard',
      theme: 'magical',
      collectibles: [
        { type: 'coin', position: [0, 4], value: 10, id: 'L60_C1' },
        { type: 'coin', position: [7, 2], value: 10, id: 'L60_C2' },
        { type: 'gem', position: [3, 3], value: 50, id: 'L60_G1' },
      ],
      minStars: 0,
      timeTarget: 75,
      stepTarget: 13,
    },
  } as EnhancedPuzzle,

  // Level 61: Stop -> teleport -> gate
  {
    grid: [
      '----xet',
      '--x---x',
      '--t-x--',
      '---x-n-',
      '--s-x--',
      '-------',
      'o--x---',
    ],
    metadata: {
      level: 61,
      name: 'Gate',
      difficulty: 'hard',
      theme: 'magical',
      collectibles: [
        { type: 'coin', position: [0, 5], value: 10, id: 'L61_C1' },
        { type: 'coin', position: [5, 3], value: 10, id: 'L61_C2' },
        { type: 'gem', position: [1, 4], value: 50, id: 'L61_G1' },
      ],
      minStars: 0,
      timeTarget: 80,
      stepTarget: 14,
    },
  } as EnhancedPuzzle,

  // Level 62: Barrier row; teleport is the only way through
  {
    grid: [
      'o---x--',
      '--x--t-',
      '-----n-',
      'xxxxxxx',
      '--s--n-',
      '-t-x---',
      '-----ex',
    ],
    metadata: {
      level: 62,
      name: 'Underbridge',
      difficulty: 'expert',
      theme: 'magical',
      collectibles: [
        { type: 'coin', position: [4, 2], value: 10, id: 'L62_C1' },
        { type: 'coin', position: [2, 4], value: 10, id: 'L62_C2' },
        { type: 'gem', position: [6, 2], value: 50, id: 'L62_G1' },
      ],
      minStars: 0,
      timeTarget: 90,
      stepTarget: 16,
    },
  } as EnhancedPuzzle,

  // Level 63: Finale
  {
    grid: [
      'o---x---',
      '----x--t',
      '--n-x---',
      '--x-x---',
      '----x---',
      '---xx-n-',
      '-t--x---',
      '----x-ex',
    ],
    metadata: {
      level: 63,
      name: 'Gate of Loops',
      difficulty: 'expert',
      theme: 'magical',
      collectibles: [
        { type: 'coin', position: [1, 7], value: 10, id: 'L63_C1' },
        { type: 'coin', position: [6, 6], value: 10, id: 'L63_C2' },
        { type: 'gem', position: [3, 2], value: 50, id: 'L63_G1' },
      ],
      minStars: 0,
      timeTarget: 110,
      stepTarget: 18,
    },
  } as EnhancedPuzzle,
];
